import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';

import { useEffect, useState } from 'react';
import { getToday } from 'src/common/helper';
import { PaginationType } from 'src/components/common/Pagin';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import InputDatePickerRange from 'src/components/common/field/InputDatePickerRange';
import { useNotify } from 'src/context/ContextNotify';
import { getAPI, postAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import PatientTab from './tabContent/PatientTab';
import PaymentTab from './tabContent/PaymentTab';
import QuoteTab from './tabContent/QuoteTab';
import ReservationTab from './tabContent/ReservationTab';

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

const a11yProps = (index: number) => {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
};

const Index = () => {
  const notifyContext = useNotify();
  const [surgerySelect, setSurgerySelect] = useState<string>('');
  const [pagination, setPagination] = useState<PaginationType>();
  const [surgeryList, setSurgeryList] = useState([]);
  const [value, setValue] = useState(0);
  const [type, setType] = useState('reserve');
  const [startDate, setStartDate] = useState<string | null>(getToday());
  const [endDate, setEndDate] = useState<string | null>(getToday());
  const [data, setData] = useState([]);

  const handleSurgeryChange = (event: any, value: any) => {
    const surgery = value.map((val: any) => val.id);

    const result = surgery.toString();
    setSurgerySelect(result);
  };

  useEffect(() => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      postAPI(`/master/surgery-search?token=${token}`, {
        page: 1,
        pagesize: 1000,
        category: null,
        status: null,
        surgery: '',
      })
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setSurgeryList(res.data.items);
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setData([]);
    getType(newValue);
  };

  const getType = (type: number) => {
    switch (type) {
      case 0:
        setType('reserve');
        break;
      case 1:
        setType('quote');
        break;
      case 2:
        setType('payment');
        break;
      case 3:
        setType('patient');
        break;
      default:
        break;
    }
  };

  const getCsvFileName = (typeName: string) => {
    let fileName = '予約情報';
    switch (typeName) {
      case 'reserve':
        fileName = '予約情報';
        break;
      case 'quote':
        fileName = '見積情報';
        break;
      case 'payment':
        fileName = '入金情報';
        break;
      case 'patient':
        fileName = '患者情報';
        break;
      default:
        break;
    }
    return fileName;
  };

  const getList = (page: number = 1) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const dateSelect =
        startDate && endDate ? `${startDate} ~ ${endDate}` : '';

      if (dateSelect === '' && type !== 'patient') {
        notifyContext.setNotifyMessage({
          show: true,
          message: 'フィルタを少なくとも一つは選択して下さい',
          type: 'error',
        });
        return;
      }

      getAPI(
        `/report/csv?type=${type}&dates=${dateSelect}&ids=${surgerySelect}&page=${page}&pagesize=10&token=${token}`
      )
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setData(res.data.items);
            setPagination(res.data.pagination);
          }
          if (res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: 'フィルタを少なくとも一つは選択して下さい',
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handlePageChange = (eventL: any, value: number) => {
    getList(value);
  };

  const handleChangeDateRange = (
    startDate: string | null,
    endDate: string | null
  ) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleSearch = () => {
    getList();
  };


  const downloadCSV = (data: string, filename: string) => {
    const csvData = new Blob([data], { type: 'text/csv' });
    const csvURL = URL.createObjectURL(csvData);
    const link = document.createElement('a');
    link.href = csvURL;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCSV = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const dateSelect =
        startDate && endDate ? `${startDate} ~ ${endDate}` : '';

      getAPI(
        `/report/export-csv?type=${type}&dates=${dateSelect}&ids=${surgerySelect}&token=${token}`
      )
        .then((res: any) => {
          downloadCSV(res, getCsvFileName(type))
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  return (
    <Stack width={'100%'}>
      <Stack
        direction={'row'}
        justifyContent={'left'}
        spacing={'8px'}
        mb={'30px'}
      >
        {value === 3 && (
          <Autocomplete
            multiple
            id='tags-outlined'
            options={surgeryList}
            getOptionLabel={(option: any) => option.surgery}
            // isOptionEqualToValue={(sur: any, value) => sur.id === value.id}
            filterSelectedOptions
            onChange={handleSurgeryChange}
            renderOption={(props, option: any) => {
              return (
                <li {...props} key={option.id}>
                  {option.surgery}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label='' placeholder='施術名' />
            )}
            sx={{
              flex: 1,
              backgroundColor: 'primary.light',
              '&.MuiAutocomplete-root .MuiOutlinedInput-root': {
                py: '7px',
              },
              '&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
                {
                  py: 0,
                  fontSize: '12px',
                },
            }}
          />
        )}
        <InputDatePickerRange
          startDate={startDate}
          endDate={endDate}
          placeholder='期間'
          sxInput={{ height: '32px', width: '240px', fontSize: '12px' }}
          onChange={handleChangeDateRange}
        />
        <BtnGradient text='検索' handleButtonClick={handleSearch} />
        <BtnOutlineGradient
          text='CSV出力'
          handleButtonClick={exportCSV}
          width='120px'
          disabled={data.length > 0 ? false : true}
        />
      </Stack>
      <Tabs
        value={value}
        onChange={handleChange}
        variant='fullWidth'
        aria-label='full width tabs'
        sx={{
          bgcolor: 'primary.light',
          '&.MuiTabs-root .MuiTabs-indicator': {
            bgcolor: '#FC6E51',
            top: 0,
          },
          '& .MuiButtonBase-root.MuiTab-root': {
            color: '#A3AED0',
            fontSize: '12px',
            fontWeight: 700,
          },
          '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
            color: '#FC6E51',
          },
        }}
      >
        <Tab label='予約' {...a11yProps(0)} />
        <Tab label='見積' {...a11yProps(1)} />
        <Tab label='支払い' {...a11yProps(2)} />
        <Tab label='患者' {...a11yProps(3)} />
      </Tabs>
      <TabPanel value={value} index={0}>
        <ReservationTab
          data={data}
          pagination={pagination}
          handlePageChange={handlePageChange}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <QuoteTab
          data={data}
          pagination={pagination}
          handlePageChange={handlePageChange}
        />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <PaymentTab
          data={data}
          pagination={pagination}
          handlePageChange={handlePageChange}
        />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <PatientTab
          data={data}
          pagination={pagination}
          handlePageChange={handlePageChange}
        />
      </TabPanel>
    </Stack>
  );
};

export default Index;
